import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Fact, NotProvided, linkTextClasses } from '@evogard/ui';
import type { ClaimCafLink, ClaimCafTracking } from '@evogard/contract';
import { cafStatusLabel, cafStatusNeedsAction, formatInstant } from '@evogard/domain';
import {
  claimFileUrl,
  useCafLink,
  useCafTracking,
  useFetchCafResult,
  useRefreshCafLink,
} from './useDesk.js';
import { Section, Wait } from './RepairBlock.js';

/**
 * O botão "Atualizar link" do "Link CAF Documentação" (16/10/2026).
 *
 * O link nasce sozinho quando o sinistro entra na Entrada; o botão gera um
 * novo quando for preciso — o anterior já foi usado, ou o associado perdeu.
 * Atualizar reescreve o link e os IDs da CAF. Depois de usado ele trava por 5
 * minutos (a API recusa igual), e a tela diz quanto falta.
 *
 * O resultado e o "Buscar resultado na CAF" moram na gaveta do bloco do alto
 * da mesa desde 20/10/2026 (`CafTracking`), e não mais nas fases.
 */
export function CafLinkAction({
  claimId,
  enabled,
}: {
  claimId: number;
  enabled: boolean;
}) {
  const state = useCafLink(claimId, true);
  const refresh = useRefreshCafLink(claimId);
  const availableAt = state.data?.availableAt ? new Date(state.data.availableAt) : null;
  const remaining = useRemaining(availableAt);
  const locked = remaining !== null;

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {enabled && (
          <Button
            variant="contorno"
            size="sm"
            icon="atualizar"
            loading={refresh.isPending}
            disabled={locked || state.isLoading}
            onClick={() => refresh.mutate()}
          >
            {state.data?.link ? 'Atualizar link' : 'Gerar link'}
          </Button>
        )}
        <span className="text-xs text-texto-suave" aria-live="polite">
          {locked
            ? `Pode ser atualizado de novo em ${remaining}.`
            : state.data?.link
              ? 'Atualizar gera um link novo na CAF e substitui o link e os IDs do anterior.'
              : 'O link nasce sozinho quando o sinistro entra na Entrada.'}
        </span>
      </div>
      {refresh.isError && <Alert tone="erro">{refresh.error.message}</Alert>}
    </div>
  );
}

/**
 * A documentação da CAF (20/10/2026), na gaveta do bloco do alto da mesa da
 * Análise: para ver depressa o que chegou. A situação e as regras, o que vale
 * ler do CAF JSON, os arquivos que a CAF mandou e o próprio JSON. Os dados do
 * associado ficam de fora — estão no SGA, e inteiros no CAF JSON.
 */
export function CafTracking({ claimId, enabled }: { claimId: number; enabled: boolean }) {
  const tracking = useCafTracking(claimId, true);
  if (tracking.isLoading) {
    return <p className="text-sm text-texto-suave">Carregando a documentação da CAF…</p>;
  }
  if (tracking.isError) return <Alert tone="erro">{tracking.error.message}</Alert>;
  const t = tracking.data;
  if (!t) return null;
  if (!t.link) {
    return (
      <Wait>
        A URL de onboarding da CAF ainda não foi gerada. Ela nasce sozinha quando o
        sinistro entra na Entrada.
      </Wait>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <Section title="Resultado">
        {t.result ? (
          <CafResult claimId={claimId} state={t} enabled={enabled} />
        ) : (
          <>
            <Wait>
              Nenhum resultado da CAF ainda. URL de onboarding gerada
              {t.generatedAt ? ` ${formatInstant(t.generatedAt)}` : ''}.
            </Wait>
            {enabled && <FetchWithoutNotice claimId={claimId} />}
          </>
        )}
        <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-3">
          {t.facts && <CafFacts facts={t.facts} />}
          <Fact label="Transaction ID" wide>
            {t.transactionId ? (
              <span className="font-mono text-xs break-all">{t.transactionId}</span>
            ) : (
              <NotProvided />
            )}
          </Fact>
        </dl>
      </Section>
      {t.result && t.result.rules.length > 0 && <CafRules rules={t.result.rules} />}
      <CafFiles claimId={claimId} tracking={t} />
    </div>
  );
}

/** O que vale ler do CAF JSON — cru, como a CAF mandou (`cafTransactionFacts`). */
function CafFacts({ facts }: { facts: NonNullable<ClaimCafTracking['facts']> }) {
  return (
    <>
      <Fact label="Documento enviado">{facts.documentType ?? <NotProvided />}</Fact>
      <Fact label="Fraude">
        {facts.fraud === null ? (
          <NotProvided />
        ) : facts.fraud ? (
          <Badge tone="erro">marcada pela CAF</Badge>
        ) : (
          'não marcada'
        )}
      </Fact>
      {facts.license && (
        <>
          <Fact label="Categoria da CNH">
            {facts.license.category ?? <NotProvided />}
          </Fact>
          <Fact label="Validade da CNH">
            {facts.license.expiresOn ?? <NotProvided />}
          </Fact>
        </>
      )}
      <Fact label="Início na CAF">
        {facts.startedAt ? (
          <span className="tabular-nums">{formatInstant(facts.startedAt)}</span>
        ) : (
          <NotProvided />
        )}
      </Fact>
      <Fact label="Último evento na CAF">
        {facts.lastEventAt ? (
          <span className="tabular-nums">{formatInstant(facts.lastEventAt)}</span>
        ) : (
          <NotProvided />
        )}
      </Fact>
      {facts.fraudScore !== null && (
        <Fact label="Pontuação de fraude (CAF)">
          <span className="tabular-nums">{facts.fraudScore}</span>
        </Fact>
      )}
    </>
  );
}

/** O resultado de cada regra, como a CAF o manda. */
// @fornecedor — as chaves são os valores de `statusReasons[].resultStatus`
const RULE_BADGE: Record<string, { tone: 'sucesso' | 'erro' | 'alerta'; text: string }> =
  {
    APPROVED: { tone: 'sucesso', text: 'passou' },
    REPROVED: { tone: 'erro', text: 'não passou' },
    PENDING: { tone: 'alerta', text: 'pendente' },
  };

/** Todas as regras da transação — o alerta do resultado lista só as que não passaram. */
function CafRules({ rules }: { rules: NonNullable<ClaimCafLink['result']>['rules'] }) {
  const passed = rules.filter((r) => r.result === 'APPROVED').length;
  return (
    <Section title="Regras da CAF" aside={`${passed} de ${rules.length} passaram`}>
      <ul className="flex flex-col rounded-borda border border-borda">
        {rules.map((r) => {
          const badge = r.result ? RULE_BADGE[r.result] : undefined;
          return (
            <li
              key={r.code}
              className="flex items-center justify-between gap-3 border-b border-borda px-3 py-1.5 text-sm last:border-b-0"
            >
              <span className="min-w-0">{r.label}</span>
              <Badge tone={badge?.tone ?? 'neutro'}>
                {badge?.text ?? r.result ?? 'sem resultado'}
              </Badge>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** Os arquivos que a CAF mandou, e o CAF JSON. Abrir assina no clique. */
function CafFiles({
  claimId,
  tracking: t,
}: {
  claimId: number;
  tracking: ClaimCafTracking;
}) {
  const open = (documentId: number) =>
    void claimFileUrl(claimId, documentId).then((url) =>
      window.open(url, '_blank', 'noopener'),
    );
  const n = t.documents.length;
  return (
    <Section
      title="Documentos que chegaram"
      aside={n === 0 ? undefined : n === 1 ? '1 arquivo' : `${n} arquivos`}
    >
      {n > 0 ? (
        <ul className="flex flex-col rounded-borda border border-borda">
          {t.documents.map((d) => (
            <li
              key={d.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-borda px-3 py-2 last:border-b-0"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <button
                  type="button"
                  className={linkTextClasses(
                    'self-start truncate text-left text-sm font-semibold',
                  )}
                  onClick={() => open(d.id)}
                >
                  {d.label}
                </button>
                <span className="text-xs text-texto-suave">
                  {d.field ? `Em ${d.field}` : 'Nos Anexos'} ·{' '}
                  <span className="tabular-nums">{formatInstant(d.receivedAt)}</span>
                </span>
              </span>
              {d.archived && <Badge tone="neutro">tirado do campo</Badge>}
            </li>
          ))}
        </ul>
      ) : (
        <Wait>
          {t.result
            ? 'Nenhum arquivo da CAF entrou neste sinistro.'
            : 'Os arquivos entram em Documentação quando a CAF der o resultado.'}
        </Wait>
      )}
      {t.json && (
        <div>
          <Button
            size="sm"
            variant="contorno"
            icon="documento"
            onClick={() => open(t.json!.id)}
          >
            Abrir o CAF JSON
          </Button>
        </div>
      )}
    </Section>
  );
}

/**
 * URL de onboarding gerada e nenhum resultado ainda (20/10/2026): o associado
 * pode não ter terminado, a URL do webhook pode ser de outro sistema, ou a
 * fila pode ter falhado. Buscar lê a transação pelo transaction ID que a
 * chamada do webhook gravou; sem ele, a API responde 409 e diz por quê.
 */
function FetchWithoutNotice({ claimId }: { claimId: number }) {
  const fetch = useFetchCafResult(claimId);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="contorno"
          size="sm"
          icon="busca"
          loading={fetch.isPending}
          onClick={() => fetch.mutate()}
        >
          Buscar resultado na CAF
        </Button>
        <span className="text-xs text-texto-suave">
          O associado já terminou o envio? O resultado entra sozinho pela chamada do
          webhook da CAF, ou busque aqui.
        </span>
      </div>
      {fetch.isError && <Alert tone="erro">{fetch.error.message}</Alert>}
    </div>
  );
}

/** O tom de cada resultado: o que pede alguém da casa é erro; a espera, alerta. */
// @valor — as chaves são `claims.caf_status`
const RESULT_TONE: Record<string, 'sucesso' | 'erro' | 'alerta' | 'informacao'> = {
  aprovado: 'sucesso',
  reprovado: 'erro',
  documento_ilegivel: 'erro',
  em_analise: 'alerta',
  em_processamento: 'informacao',
};

/**
 * O resultado da CAF (16/10/2026), no alto da gaveta da CAF: a situação, as
 * regras que não passaram e os documentos que entraram. Reprovado ou ilegível
 * diz as duas saídas — atualizar o link na Entrada para o associado reenviar,
 * ou coletar à mão em Documentação. "Buscar resultado na CAF" consulta de novo quando o aviso da
 * CAF não chegou.
 */
function CafResult({
  claimId,
  state,
  enabled,
}: {
  claimId: number;
  state: ClaimCafLink;
  enabled: boolean;
}) {
  const fetch = useFetchCafResult(claimId);
  const result = state.result;
  const failing = result?.rules.filter((r) => r.result !== 'APPROVED') ?? [];
  const tone = result ? (RESULT_TONE[result.status] ?? 'informacao') : 'informacao';

  return (
    <div className="flex flex-col gap-2">
      <Alert
        tone={tone}
        title={
          result
            ? `${cafStatusLabel(result.status)} · ${formatInstant(result.at)}`
            : 'O associado terminou o envio — o resultado da CAF ainda não chegou'
        }
      >
        {result && cafStatusNeedsAction(result.status) && (
          <p>
            Os documentos precisam ser enviados de novo: atualize o link na Entrada e
            mande ao associado, ou faça a coleta manual e anexe em Documentação.
          </p>
        )}
        {result?.status === 'em_analise' && (
          <p>
            A CAF mandou os documentos para revisão manual. O resultado chega sozinho.
          </p>
        )}
        {failing.length > 0 && (
          <ul className="mt-1 list-disc pl-5">
            {failing.map((r) => (
              <li key={r.code}>
                {r.label}
                {r.result === 'PENDING' ? ' (pendente)' : ''}
              </li>
            ))}
          </ul>
        )}
        {result && result.documents > 0 && (
          <p className="mt-1 text-xs">
            {result.documents === 1
              ? '1 documento entrou em Documentação.'
              : `${result.documents} documentos entraram em Documentação.`}
          </p>
        )}
        {result && result.failed.length > 0 && (
          <p className="mt-1 text-xs">
            Não entraram: {result.failed.join(', ')}. Eles estão no CAF JSON, nos anexos.
          </p>
        )}
      </Alert>
      {enabled && (
        <div>
          <Button
            variant="contorno"
            size="sm"
            icon="busca"
            loading={fetch.isPending}
            onClick={() => fetch.mutate()}
          >
            Buscar resultado na CAF
          </Button>
        </div>
      )}
      {fetch.isError && <Alert tone="erro">{fetch.error.message}</Alert>}
    </div>
  );
}

/** "4 min", "1 min" — o que falta para a trava vencer, contado na tela. */
function useRemaining(until: Date | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [until]);
  if (!until) return null;
  const ms = until.getTime() - now;
  if (ms <= 0) return null;
  const minutes = Math.ceil(ms / 60_000);
  return `${minutes} min`;
}
